import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import type { PlotRequestFilterValues } from "@/features/map/types";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";

interface PlotRequestFilterChipsProps {
  filters: PlotRequestFilterValues;
  onFilterChange: (filters: PlotRequestFilterValues) => void;
  onClearAll: () => void;
}

/**
 * 適用中のフィルター条件を表示するチップ群
 */
export const PlotRequestFilterChips: React.FC<PlotRequestFilterChipsProps> = ({
  filters,
  onFilterChange,
  onClearAll,
}) => {
  const { availableFields } = useAdditionalFieldsConfig();

  // カスタム項目のフィルターが設定されているかチェック
  const hasAdditionalFieldsFilters = React.useMemo(() => {
    if (!filters.additionalFields) return false;
    return Object.values(filters.additionalFields).some(
      (value) => value !== ""
    );
  }, [filters.additionalFields]);

  const hasFilters =
    !!filters.createdAtFrom ||
    !!filters.createdAtTo ||
    !!filters.updatedAtFrom ||
    !!filters.updatedAtTo ||
    filters.tags.length > 0 ||
    filters.status.length > 0 ||
    filters.type.length > 0 ||
    !!filters.requestAddress ||
    !!filters.requestLocation ||
    !!filters.requestChiban ||
    !!filters.requestKaokuNumber ||
    !!filters.remarks ||
    hasAdditionalFieldsFilters;

  if (!hasFilters) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">適用中の検索条件</Typography>
        <Button
          size="small"
          onClick={onClearAll}
          sx={{ color: "primary.main" }}
        >
          × すべてクリア
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {filters.createdAtFrom && (
          <Chip
            label={`作成日時: ${format(
              parseISO(filters.createdAtFrom),
              "yyyy/MM/dd HH:mm"
            )} 以降`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, createdAtFrom: null });
            }}
          />
        )}
        {filters.createdAtTo && (
          <Chip
            label={`作成日時: ${format(
              parseISO(filters.createdAtTo),
              "yyyy/MM/dd HH:mm"
            )} 以前`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, createdAtTo: null });
            }}
          />
        )}
        {filters.updatedAtFrom && (
          <Chip
            label={`更新日時: ${format(
              parseISO(filters.updatedAtFrom),
              "yyyy/MM/dd HH:mm"
            )} 以降`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, updatedAtFrom: null });
            }}
          />
        )}
        {filters.updatedAtTo && (
          <Chip
            label={`更新日時: ${format(
              parseISO(filters.updatedAtTo),
              "yyyy/MM/dd HH:mm"
            )} 以前`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, updatedAtTo: null });
            }}
          />
        )}
        {filters.tags.map((tag) => (
          <Chip
            key={tag.id}
            label={`タグ: ${tag.name}`}
            size="small"
            onDelete={() => {
              onFilterChange({
                ...filters,
                tags: filters.tags.filter((t) => t.id !== tag.id),
              });
            }}
          />
        ))}
        {filters.status.map((status) => (
          <Chip
            key={status}
            label={`ステータス: ${status === "SUCCEED" ? "成功" : "失敗"}`}
            size="small"
            onDelete={() => {
              onFilterChange({
                ...filters,
                status: filters.status.filter((s) => s !== status),
              });
            }}
          />
        ))}
        {filters.type.map((type) => {
          const typeLabel = type;
          return (
            <Chip
              key={type}
              label={`種別: ${typeLabel}`}
              size="small"
              onDelete={() => {
                onFilterChange({
                  ...filters,
                  type: filters.type.filter((t) => t !== type),
                });
              }}
            />
          );
        })}
        {filters.requestAddress && (
          <Chip
            label={`住居表示: ${filters.requestAddress}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, requestAddress: null });
            }}
          />
        )}
        {filters.requestLocation && (
          <Chip
            label={`所在: ${filters.requestLocation}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, requestLocation: null });
            }}
          />
        )}
        {filters.requestChiban && (
          <Chip
            label={`地番: ${filters.requestChiban}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, requestChiban: null });
            }}
          />
        )}
        {filters.requestKaokuNumber && (
          <Chip
            label={`家屋番号: ${filters.requestKaokuNumber}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, requestKaokuNumber: null });
            }}
          />
        )}
        {filters.remarks && (
          <Chip
            label={`備考: ${filters.remarks}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, remarks: null });
            }}
          />
        )}
        {/* カスタム項目のフィルター */}
        {availableFields?.fields &&
          filters.additionalFields &&
          availableFields.fields.map((field) => {
            const value = filters.additionalFields?.[field.id];
            // 値が空文字列またはundefinedの場合は表示しない
            // ただし、真偽値フィールドの"false"は表示する
            if (
              !value &&
              !(field.fieldType === "boolean" && value === "false")
            ) {
              return null;
            }

            // 真偽値フィールドの表示ラベル
            let displayValue = value;
            if (field.fieldType === "boolean") {
              displayValue = value === "true" ? "はい" : "いいえ";
            } else if (field.fieldType === "date" && value) {
              // 日付フィールドの表示フォーマット: YYYY-MM-DD → YYYY/MM/DD
              try {
                const parsed = parseISO(value);
                if (!isNaN(parsed.getTime())) {
                  displayValue = format(parsed, "yyyy/MM/dd");
                }
              } catch {
                displayValue = value;
              }
            } else if (field.fieldType === "datetime" && value) {
              // 日時フィールドの表示フォーマット: YYYY-MM-DDTHH:mm → YYYY/MM/DD HH:mm
              try {
                const parsed = parseISO(value);
                if (!isNaN(parsed.getTime())) {
                  displayValue = format(parsed, "yyyy/MM/dd HH:mm");
                }
              } catch {
                displayValue = value;
              }
            }

            return (
              <Chip
                key={field.id}
                label={`${field.key}: ${displayValue}`}
                size="small"
                onDelete={() => {
                  // 動的キー削除(no-dynamic-delete)を避けるため、分割代入で対象キーを除外する。
                  const { [field.id]: _removed, ...newAdditionalFields } = {
                    ...filters.additionalFields,
                  };
                  onFilterChange({
                    ...filters,
                    additionalFields: newAdditionalFields,
                  });
                }}
              />
            );
          })}
      </Box>
    </Box>
  );
};
